import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import AppV2 from './AppV2';
import { brand, tours } from './data';
import './styles.css';
import './tour-pages.css';
import './source-parity.css';
import './parity-fixes.css';
import './source-zero-block.css';
import './design-parity-v2.css';
import './mobile-header-source.css';
import './homepage-source-contact.css';

function HeaderSocialParityBridge() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    let observer: MutationObserver | null = null;
    let attempts = 0;

    const apply = () => {
      const actions = document.querySelector<HTMLElement>('.site-header .header-actions');
      if (!actions) return false;

      const whatsapp = actions.querySelector<HTMLAnchorElement>('.social-whatsapp');
      const telegram = actions.querySelector<HTMLAnchorElement>('.social-telegram');
      const instagram = actions.querySelector<HTMLAnchorElement>('.social-instagram');
      const menu = actions.querySelector<HTMLElement>('.menu-button');
      let max = actions.querySelector<HTMLAnchorElement>('.social-max');

      if (!max && instagram) {
        instagram.classList.remove('social-instagram');
        instagram.classList.add('social-max');
        instagram.href = brand.maxMessenger;
        instagram.setAttribute('aria-label', 'MAX');
        instagram.innerHTML = '<span class="max-mark">MAX</span>';
        max = instagram;
      }

      if (!max || !telegram || !whatsapp) return false;

      const desired = [max, telegram, whatsapp, menu].filter(Boolean) as HTMLElement[];
      desired.forEach((node, index) => {
        if (actions.children[index] !== node) actions.insertBefore(node, actions.children[index] || null);
      });
      return true;
    };

    const timer = window.setInterval(() => {
      attempts += 1;
      if (cancelled) return;
      if (apply() || attempts >= 80) {
        window.clearInterval(timer);
        const actions = document.querySelector<HTMLElement>('.site-header .header-actions');
        if (actions) {
          observer = new MutationObserver(() => {
            if (!cancelled) apply();
          });
          observer.observe(actions, { childList: true, subtree: true });
        }
      }
    }, 25);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      observer?.disconnect();
    };
  }, [location.pathname]);

  return null;
}

function SocialAndFooterParityBridge() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const apply = () => {
      const footerSocials = document.querySelector<HTMLElement>('.footer .footer-socials');
      if (!footerSocials) return false;

      document.querySelectorAll<HTMLAnchorElement>('a.social-instagram').forEach((link) => {
        link.href = 'https://www.instagram.com/maxtour__vietnam/';
      });

      let max = footerSocials.querySelector<HTMLAnchorElement>('.social-max');
      if (!max) {
        max = document.createElement('a');
        max.className = 'social-link social-max';
        max.href = brand.maxMessenger;
        max.target = '_blank';
        max.rel = 'noreferrer';
        max.setAttribute('aria-label', 'MAX');
        max.innerHTML = '<span class="max-mark">MAX</span>';
        footerSocials.append(max);
      }
      return true;
    };

    const timer = window.setInterval(() => {
      attempts += 1;
      if (cancelled) return;
      if (apply() || attempts >= 80) window.clearInterval(timer);
    }, 25);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [location.pathname]);

  return null;
}

function HomeSeoParityBridge() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/') return;
    const timer = window.setTimeout(() => {
      const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (meta) meta.content = 'Экскурсии в Нячанге';
    }, 50);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  return null;
}

const sourceFaqAnswers = new Map<string, string>([
  ['Нужна ли предоплата?', 'Да, для того чтобы забронировать тур заранее, Вам нужно внести депозит в размере 30-100% от стоимости экскурсии (наличными, картой, переводом в рублях/тенге/долларах и других валютах), оставшуюся сумму нужно будет оплатить в день экскурсии Гиду в донгах.'],
  ['Какие правила отмены и переноса?', 'Перенос тура - бесплатно до 17:00 за день до экскурсии. После этого удерживается 30% от стоимости. Отмена тура: бесплатно более чем за 48 часов до выезда; до 17:00 за день до выезда - удержание 30%; в день выезда или при неявке - удержание 100%. Форс-мажор: тур может быть изменён или отменён из-за природных катастроф, эпидемий и других обстоятельств, не зависящих от компании. В таких случаях возможен возврат средств или перенос тура. Если вы не можете поехать по причине болезни - возврат возможен при предоставлении медицинских документов. Возврат средств - в течение 7 рабочих дней (возможны банковские комиссии). Важно: небольшой дождь не является причиной для отмены тура.'],
  ['Какие способы оплаты доступны?', 'Мы предлагаем несколько удобных способов оплаты: наличными, наш менеджер приедет к вам в отель, примет оплату и выдаст квитанцию; банковской картой; переводом по СБП (Система быстрых платежей) или Каспи; банковским переводом в рублях или тенге; международными переводами. Если у вас есть предпочтительный способ оплаты, свяжитесь с нами - мы подберем наиболее удобный вариант для вас.'],
  ['А что будет, если группа не наберется?', 'Если Вы внесли депозит, но группа к назначенной дате была не собрана, мы вернем депозит 100% обратно. Обычно при бронировании заранее все группы для выезда собираются полностью без каких либо проблем.'],
  ['Возможны ли дополнительные расходы?', 'Большинство наших экскурсий уже включает все основные услуги. Дополнительная оплата потребуется только в случае проживания в отдаленных от центра Нячанга районах. Стоимость дополнительного трансфера (за автомобиль): Камрань - 30$ (1-6 чел.) / 50$ (7-14 чел.); Diamond Bay, Amiana, Alibu, Театр DO - 20$ (1-6 чел.) / 30$ (7-14 чел.); Зоклет, GM Resort, Paradise - 60$ (1-6 чел.) / 90$ (7-14 чел.). Если вы не уверены, относится ли ваш отель к этим районам, просто напишите нам, и мы с радостью подскажем.'],
]);

function FaqSourceParityBridge() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const apply = () => {
      const faq = document.querySelector<HTMLElement>('.faq-section');
      if (!faq) return false;

      faq.querySelectorAll<HTMLDetailsElement>('details').forEach((item) => {
        const summary = item.querySelector<HTMLElement>('summary');
        const answer = item.querySelector<HTMLParagraphElement>('p');
        if (!summary || !answer) return;
        const question = (summary.textContent || '').replace('＋', '').trim();
        const exact = sourceFaqAnswers.get(question);
        if (exact) answer.textContent = exact;
      });
      return true;
    };

    const timer = window.setInterval(() => {
      attempts += 1;
      if (cancelled) return;
      if (apply() || attempts >= 80) window.clearInterval(timer);
    }, 25);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [location.pathname]);

  return null;
}

function HomeDesignParityBridge() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/') return;

    let cancelled = false;
    const mounted: HTMLElement[] = [];
    const appendedFaq: HTMLElement[] = [];

    const mount = async () => {
      let faq: HTMLElement | null = null;
      for (let attempt = 0; attempt < 40 && !cancelled; attempt += 1) {
        faq = document.querySelector<HTMLElement>('.faq-section');
        if (faq?.parentElement) break;
        await new Promise((resolve) => window.setTimeout(resolve, 25));
      }
      if (cancelled || !faq?.parentElement) return;

      document.getElementById('source-home-brand-band')?.remove();
      document.getElementById('source-home-reviews')?.remove();
      document.getElementById('source-home-manager-contact')?.remove();
      document.getElementById('source-home-fast-track-terms')?.remove();

      const couponActions = document.querySelector<HTMLElement>('.coupon-section .coupon-actions');
      if (couponActions) {
        couponActions.innerHTML = `<a class="button button-primary source-coupon-manager" href="${brand.managerTelegram}" target="_blank" rel="noreferrer">СВЯЗАТЬСЯ С МЕНЕДЖЕРОМ</a>`;
      }

      const fastTrackCopy = document.querySelector<HTMLElement>('.fast-track-section .fast-track-copy');
      const fastTrackButton = fastTrackCopy?.querySelector<HTMLAnchorElement>('.button');
      if (fastTrackCopy && fastTrackButton) {
        const terms = document.createElement('div');
        terms.id = 'source-home-fast-track-terms';
        terms.className = 'source-home-fast-track-terms';
        terms.innerHTML = '<p><strong>Что входит:</strong></p><ul><li>Встреча с табличкой у зоны таможенного контроля и проход через паспортный контроль без очереди</li><li>Индивидуальный трансфер до отеля в центре Нячанга</li></ul><p>Для 2 человек - 65$ за двоих.</p><p>Для 4 человек - 110$ за четверых.</p>';
        fastTrackButton.insertAdjacentElement('beforebegin', terms);
        fastTrackButton.href = brand.managerTelegram;
        fastTrackButton.textContent = 'СВЯЗАТЬСЯ С МЕНЕДЖЕРОМ';
        fastTrackButton.classList.add('source-fast-track-contact');
        mounted.push(terms);
      }

      const benefitCopy = [
        'Большой выбор круизов, морских прогулок, катеров и яхт. Подберём идеальный вариант для отдыха, праздника или индивидуального путешествия',
        'Новые автомобили и комфортабельные автобусы. Забираем из вашего отеля и доставляем обратно после экскурсии',
        'Честные и доступные цены без скрытых платежей и доплат во время экскурсии',
        'Небольшие группы позволяют путешествовать комфортно, лучше слышать гида и наслаждаться экскурсией без ощущения массового тура',
        'Любую экскурсию можем провести в индивидуальном формате, полностью адаптировав её под ваши запросы',
      ];
      document.querySelectorAll<HTMLElement>('.benefits-section .benefit-item p').forEach((node, index) => {
        if (benefitCopy[index]) node.textContent = benefitCopy[index];
      });

      const faqList = faq.querySelector<HTMLElement>('.faq-list');
      if (faqList && faqList.children.length < 7) {
        const first = document.createElement('details');
        first.className = 'source-home-added-faq';
        first.innerHTML = '<summary>Как записаться на экскурсию?<span>＋</span></summary><p>Оставьте заявку на сайте или свяжитесь с нами через удобный мессенджер. Мы быстро ответим, поможем выбрать экскурсию, подберём удобную дату и подтвердим бронирование. Рекомендуем бронировать заранее, особенно в высокий туристический сезон.</p>';
        faqList.insertBefore(first, faqList.firstChild);
        appendedFaq.push(first);

        const last = document.createElement('details');
        last.className = 'source-home-added-faq';
        last.innerHTML = '<summary>MAX TOUR - официальная компания?<span>＋</span></summary><p>Да, MAX TOUR работает официально и имеет все необходимые разрешения для организации экскурсий. Мы проводим туры по проверенным маршрутам, соблюдаем стандарты безопасности и обеспечиваем страхование для каждого участника. Ваш комфорт и безопасность - наш главный приоритет.</p>';
        faqList.append(last);
        appendedFaq.push(last);
      }

      const contact = document.createElement('section');
      contact.id = 'source-home-manager-contact';
      contact.className = 'source-home-manager-contact';
      contact.innerHTML = `<div class="container"><div class="source-home-manager-contact-inner"><h2>Выберите, где вам удобнее общаться с менеджером</h2><div class="source-home-manager-contact-actions"><a class="messenger-button whatsapp-button" href="${brand.whatsapp}" target="_blank" rel="noreferrer"><span aria-hidden="true">◉</span>WhatsApp</a><a class="messenger-button telegram-button" href="${brand.managerTelegram}" target="_blank" rel="noreferrer"><span aria-hidden="true">➤</span>Telegram</a><a class="messenger-button max-button" href="${brand.maxMessenger}" target="_blank" rel="noreferrer"><span class="max-mark" aria-hidden="true">MAX</span>MAX</a></div></div></div>`;
      faq.insertAdjacentElement('afterend', contact);
      mounted.push(contact);
    };

    void mount();
    return () => {
      cancelled = true;
      mounted.forEach((node) => node.remove());
      appendedFaq.forEach((node) => node.remove());
    };
  }, [location.pathname]);

  return null;
}

function TourMainSourceBridge() {
  const location = useLocation();

  useEffect(() => {
    const slug = location.pathname.split('/').filter(Boolean).at(-1) || '';
    if (!tours.some((tour) => tour.slug === slug)) return;

    let cancelled = false;
    let generated: HTMLElement | null = null;
    const hiddenFallbacks: HTMLElement[] = [];
    const controller = new AbortController();

    const mount = async () => {
      try {
        const response = await fetch(`/tour-main/${encodeURIComponent(slug)}.html`, {
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!response.ok) return;
        const html = await response.text();
        if (cancelled || !html.includes('t396__artboard')) return;

        let anchor: HTMLElement | null = null;
        for (let attempt = 0; attempt < 40 && !cancelled; attempt += 1) {
          anchor = document.querySelector<HTMLElement>('.source-tour-main');
          if (anchor?.parentElement) break;
          await new Promise((resolve) => window.setTimeout(resolve, 25));
        }
        if (cancelled || !anchor?.parentElement) return;

        generated = document.createElement('section');
        generated.className = 'source-original-main source-generated-main';
        generated.dataset.sourceSlug = slug;
        generated.innerHTML = `<div class="source-zero-block">${html}</div>`;
        anchor.parentElement.insertBefore(generated, anchor);

        document.querySelectorAll<HTMLElement>('.source-tour-main').forEach((node) => {
          node.classList.add('source-main-fallback-hidden');
          hiddenFallbacks.push(node);
        });
      } catch (error) {
        if (!controller.signal.aborted) console.warn('Exact tour source block unavailable; using React fallback.', error);
      }
    };

    void mount();
    return () => {
      cancelled = true;
      controller.abort();
      generated?.remove();
      hiddenFallbacks.forEach((node) => node.classList.remove('source-main-fallback-hidden'));
    };
  }, [location.pathname]);

  return null;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <HeaderSocialParityBridge />
      <SocialAndFooterParityBridge />
      <HomeSeoParityBridge />
      <FaqSourceParityBridge />
      <HomeDesignParityBridge />
      <TourMainSourceBridge />
      <AppV2 />
    </BrowserRouter>
  </React.StrictMode>,
);
